import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { TileTypes } from 'generated/graphql-types';
import { EMPTY, forkJoin, merge, ObservableInput, of } from 'rxjs';
import { catchError, debounceTime, exhaustMap, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { addTileFailure, addTileSuccess } from '../../dashboard/add-dashboard-tile/state/add-dashboard-tile.actions';
import { DashboardTileUnionType, VerboseTileIds } from '../../dashboard/models/dashboard-tile.model';
import * as DashboardActions from '../../dashboard/state/dashboard.actions';
import { getFullscreenTile, getPreviewTile, getSelectedDashboard } from '../../dashboard/state/dashboard.selectors';
import { ExecuteTilePostModel } from '../models/execute-tile-post.model';
import { TileDataUnion } from '../models/tile-data.model';
import { getTileViewMode } from '../models/tile-view-mode.model';
import { TileHttpService } from '../services/tile-http.service';
import { TileApiService } from '../services/tile.api.service';
import * as TileActions from './tile.actions';
import { tileSave } from './tile.actions';
import { getTileLoadBatchQueue } from './tiles.selectors';

@Injectable()
export class TileEffects {
   tileLoad = createEffect(() => {
      return this.actions$.pipe(
         ofType(TileActions.tileLoad),
         debounceTime(50),
         withLatestFrom(
            this.store.pipe(select(getSelectedDashboard)),
            this.store.pipe(select(getFullscreenTile)),
            this.store.pipe(select(getPreviewTile)),
            this.store.pipe(select(getTileLoadBatchQueue))
         ),
         mergeMap(([action, dashboard, fullscreenTile, previewTile, batchQueue]) => {
            if (!dashboard) {
               throw new Error('Could not find selected dashboard');
            }
            if (!batchQueue[action.dashboardTileId]) batchQueue[action.dashboardTileId] = { ...action };

            const queries: { query: string; variables: { params: ExecuteTilePostModel } }[] = [];
            const orderedBatchQueueInfo = Object.keys(batchQueue)
               .map((k) => {
                  const batchQueueInfo = batchQueue[k];
                  let foundTile: DashboardTileUnionType | null =
                     dashboard.dashboardTiles.find((t) => t.dashboardTileId === batchQueueInfo.dashboardTileId) ?? null;

                  const isFullscreenTile = batchQueueInfo.dashboardTileId === VerboseTileIds.fullscreenTile;
                  if (isFullscreenTile) foundTile = fullscreenTile;
                  const isPreviewTile = batchQueueInfo.dashboardTileId === VerboseTileIds.previewTile;
                  if (isPreviewTile) foundTile = previewTile;

                  if (!foundTile) {
                     throw new Error(`Could not find ${batchQueueInfo.dashboardTileId} tile selected dashboard`);
                  }

                  return { tile: foundTile, batchQueueInfo, isFullscreenTile, isPreviewTile };
               })
               .sort((a, b) => {
                  if (!a.tile || a.tile.totalTimesProcessed === 0) return -1;
                  if (!b.tile || b.tile.totalTimesProcessed === 0) return 1;

                  const k1ProcessingTimeAverage = a.tile.totalMillisecondsProcessed / a.tile.totalTimesProcessed;
                  const k2ProcessingTimeAverage = b.tile.totalMillisecondsProcessed / b.tile.totalTimesProcessed;

                  return k1ProcessingTimeAverage > k2ProcessingTimeAverage ? 1 : -1;
               });

            orderedBatchQueueInfo.forEach(({ tile, batchQueueInfo, isFullscreenTile, isPreviewTile }) => {
               try {
                  const tileLoadInfo = this.tileService.getTileLoadInfo(
                     tile,
                     getTileViewMode(
                        tile,
                        batchQueueInfo.isFullscreen || isFullscreenTile,
                        batchQueueInfo.forceViewModeRecalculation || isPreviewTile
                     ),
                     batchQueueInfo.pageNumber,
                     batchQueueInfo.pageSize
                  );
                  queries.push({
                     query: tileLoadInfo.query,
                     variables: { params: tileLoadInfo.params },
                  });
                  if (tile.tile.typeId !== TileTypes.Clock) {
                     tileLoadInfo.params = this.tileHttpService.appendTimeZoneTileParameter(tileLoadInfo.params);
                  }
               } catch (e) {
                  console.error(e);
               }
            });

            // Seperating batched queries into equal "buckets" so multiple batch queries can be made reducing potentially long initial load time
            // Currently building 5 buckets to give some leeway since chrome starts limiting concurrent requests at 7
            const bucketCount = 5;
            const buckets: { query: string; variables: { params: ExecuteTilePostModel } }[][] = [];
            for (let i = 0; i < bucketCount; i++) {
               buckets[i] = [];
            }

            const queriesPerBucket = Math.floor((queries.length ?? 0) / bucketCount);
            const numberOfBucketsThatNeedAnExtraQuery = (queries.length ?? 0) % bucketCount;
            let currBucketIdx = 0;
            let currQueryIdx = 0;

            // Add queriesPerBucket to each bucket that does not account for remainders
            for (let i = 0; i < bucketCount - numberOfBucketsThatNeedAnExtraQuery; i++) {
               buckets[currBucketIdx] = queries.slice(currQueryIdx, currQueryIdx + queriesPerBucket);
               currQueryIdx += queriesPerBucket;
               currBucketIdx++;
            }
            // Add queriesPerBucket + 1 to each bucket that does account for remainders
            for (let i = 0; i < numberOfBucketsThatNeedAnExtraQuery; i++) {
               buckets[currBucketIdx] = queries.slice(currQueryIdx, currQueryIdx + queriesPerBucket + 1);
               currQueryIdx += queriesPerBucket + 1;
               currBucketIdx++;
            }

            const queries$: ObservableInput<Action<any>>[] = buckets.map((b) => {
               if (b.length === 0) return EMPTY;
               return forkJoin([this.apiService.queryBatch<ExecutionResult<TileDataUnion>[]>(...b)]).pipe(
                  exhaustMap((sources: [ExecutionResult<TileDataUnion>[]]) => {
                     const first = Array.isArray(sources[0]) ? sources[0] : [sources[0]];
                     return first.map((s) => {
                        const parsedResponse = s.data[Object.keys(s.data)[0]];

                        if (s.errors) {
                           return TileActions.tileLoadedFailure({
                              error: s.errors,
                              dashboardTileId: parsedResponse.dashboardTileId,
                           });
                        }

                        return TileActions.tileLoadedSuccess({
                           tileData: parsedResponse,
                           dashboardTileId: parsedResponse.dashboardTileId,
                        });
                     });
                  }),
                  catchError((e) => {
                     const errors = Array.isArray(e.error) ? e.error : [e.error];
                     const errorActions = errors.map((res, i) => {
                        const { dashboardTileId } = b[i].variables.params;
                        if (dashboardTileId) {
                           if (res.errors) {
                              return TileActions.tileLoadedFailure({
                                 error: res.errors,
                                 dashboardTileId,
                              });
                           } else {
                              return TileActions.tileLoadedSuccess({
                                 tileData: res.data[Object.keys(res.data)[0]],
                                 dashboardTileId: dashboardTileId,
                              });
                           }
                        }
                     });

                     return [...errorActions];
                  })
               );
            });
            queries$.push(of(TileActions.clearTileBatchQueue()));

            return merge(...queries$);
         })
      );
   });

   tileSave = createEffect(() => {
      return this.actions$.pipe(
         ofType(tileSave),
         withLatestFrom(this.store.pipe(select(getSelectedDashboard))),
         switchMap(([action, selectedDashboard]) => {
            if (!selectedDashboard) return EMPTY;
            const isNewTile = action.dashboardTileId <= 0;
            return this.tileService
               .saveTile(action.dashboardTileId, action.parameters, action.tileId, selectedDashboard.dashboardId)
               .pipe(
                  map((response) => {
                     return isNewTile
                        ? addTileSuccess({ dashboardTileId: action.dashboardTileId, tileId: action.tileId })
                        : DashboardActions.updateDashboardTileOnSelectedDashboardById({
                             dashboardTileId: action.dashboardTileId,
                          });
                  }),
                  catchError((error) => {
                     return of(
                        isNewTile
                           ? addTileFailure(error)
                           : TileActions.tileLoadedFailure({ error, dashboardTileId: action.dashboardTileId })
                     );
                  })
               );
         })
      );
   });

   refreshTilesThatNeedDataToIncludeMappingsDoneFromTileSaveSuccess = createEffect(() => {
      return this.actions$.pipe(
         ofType(TileActions.tileSaveSuccess),
         withLatestFrom(this.store.select(getSelectedDashboard)),
         switchMap(([action, selectedDashboard]) => {
            const actions: Action[] = [];

            selectedDashboard?.dashboardTiles.forEach((dt) => {
               actions.push(
                  DashboardActions.updateDashboardTileOnSelectedDashboardById({
                     dashboardTileId: dt.dashboardTileId,
                  })
               );
            });
            return actions;
         })
      );
   });

   tileDelete = createEffect(() => {
      return this.actions$.pipe(
         ofType(TileActions.deleteDashboardTile),
         switchMap((action) => {
            return this.tileService.deleteTile(action.dashboardTileId).pipe(
               map(() => {
                  return TileActions.deleteDashboardTileSuccess({ dashboardTileId: action.dashboardTileId });
               }),
               catchError((error) => {
                  return of(TileActions.deleteDashboardTileFailure({ error }));
               })
            );
         })
      );
   });

   constructor(
      private tileService: TileApiService,
      private actions$: Actions,
      private store: Store,
      private apiService: LcsApiService,
      private tileHttpService: TileHttpService
   ) {}
}
